import React, { useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
    Button,
    Grid,
    Menu,
    MenuItem,
    CircularProgress,
} from '@material-ui/core';

import { useTranslation } from 'react-i18next';
import ConfirmModal from '../../ConfirmModal';
import {
    BUTTON_DANGER_COLOR,
    BUTTON_DEFAULT_COLOR,
} from '../../Button/constants';
import { noop } from '../../utils';
import {
    REMOVE_PHOTO,
    REMOVE_POST,
    REPORT_TYPE_PHOTO,
    REPORT_TYPE_POST,
    EDIT_POST,
    REPORT_TYPE_CAPTION,
    REPORT_TYPE_COMMENT,
} from './constants';
import StyledCommentModal from '../../LogAction/CommentModal';
import Modal from '../../Modal';
import {
    activityDataRequested,
    editActivityRequested,
    removePhotoRequested,
    removePostRequested,
} from '../../../activities/actions';
import { connect } from 'react-redux';
import { getActiveCommunityId } from '../../../user/selectors';
import { ACTIVITIES_FEED_TYPE_TRENDING } from '../../../activities/constants';

const ActivityOptionsPopper = ({
    content,
    options,
    onSelect,
    isLoading,
    activityId,
    actionId,
    updateActivity,
    removePhoto,
    removePost,
    communityId,
    activity,
    ...props
}) => {
    const { t } = useTranslation();
    const [anchorEl, setAnchorEl] = React.useState(null);
    const [isConfirmationShown, setConfirmationShown] = useState(false);
    const [optionId, setSelectedOptionId] = useState(null);
    const [isActionLoading, setIsActionLoading] = useState(false);

    const handleClose = useCallback(() => {
        setAnchorEl(null);
        setConfirmationShown(false);
        setIsActionLoading(false);
    }, []);

    useEffect(() => {
        if (!isLoading) {
            handleClose();
        }
    }, [isLoading, handleClose]);

    const open = Boolean(anchorEl);
    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const id = 'demo-positioned-button';
    const captionComment =
        activity && activity.comments
            ? activity.comments.find((comment) => comment.isCaption)
            : null;

    const handleRemovePhoto = useCallback(() => {
        setIsActionLoading(true);
        removePhoto({
            actionId,
            communityId,
            activityId,
        });
        setConfirmationShown(false);
    }, [actionId, communityId, removePhoto, activityId]);

    const handleRemovePost = useCallback(() => {
        setIsActionLoading(true);
        removePost({
            actionId,
            communityId,
            activityId,
        });
        setConfirmationShown(false);
    }, [actionId, communityId, removePost, activityId]);

    const handleSubmit = useCallback(
        (message, imageFile, imageMD5) => {
            const options = {};
            if (message?.length > 0) {
                options.message = message;
            }
            if (imageFile && imageMD5) {
                options.photoMD5 = imageMD5;
                options.file = imageFile;
            }
            setIsActionLoading(true);
            updateActivity({
                actionId,
                communityId,
                options,
                activityId,
            });
            setConfirmationShown(false);
        },
        [actionId, communityId, updateActivity, activityId],
    );
    const getConfirmationModal = useCallback(() => {
        const getModalProps = () => {
            switch (optionId) {
                case REPORT_TYPE_POST:
                case REPORT_TYPE_PHOTO:
                case REPORT_TYPE_CAPTION:
                case REPORT_TYPE_COMMENT:
                    return {
                        title: t(
                            `flag.${
                                optionId === REPORT_TYPE_CAPTION
                                    ? 'caption'
                                    : optionId === REPORT_TYPE_COMMENT
                                    ? 'comment'
                                    : 'descriptor'
                            }`,
                        ),
                        confirmBtnText: t('flag.descriptor'),
                        confirmBtnType: BUTTON_DANGER_COLOR,
                        onConfirm: () => onSelect(content, optionId),
                        children: (
                            <Grid container direction="column">
                                <Grid item>
                                    {t('flag.reportConfirmationDescription')}
                                </Grid>
                            </Grid>
                        ),
                    };
                case REMOVE_PHOTO:
                    return {
                        title: t('removePhoto.descriptor'),
                        confirmBtnText: t('removePhoto.descriptor'),
                        confirmBtnType: BUTTON_DANGER_COLOR,
                        onConfirm: handleRemovePhoto,
                        children: (
                            <Grid container direction="column">
                                <Grid item>
                                    {t('removePhoto.removePhotoConfirmation')}
                                </Grid>
                            </Grid>
                        ),
                    };
                case REMOVE_POST:
                    return {
                        title: t('removePost.descriptor'),
                        confirmBtnText: t('removePost.descriptor'),
                        confirmBtnType: BUTTON_DANGER_COLOR,
                        onConfirm: handleRemovePost,
                        children: (
                            <Grid container direction="column">
                                <Grid item>
                                    {t('removePost.removePostConfirmation')}
                                </Grid>
                            </Grid>
                        ),
                    };
                default:
                    return null;
            }
        };

        const modalProps = getModalProps();

        if (modalProps) {
            return (
                <ConfirmModal
                    isOpen={isConfirmationShown}
                    cancelBtnText={t('cancel')}
                    cancelBtnType={BUTTON_DEFAULT_COLOR}
                    onClose={handleClose}
                    onCancel={handleClose}
                    confirmBtnLoading={isLoading}
                    confirmBtnDisabled={isLoading}
                    cancelBtnDisabled={isLoading}
                    {...modalProps}
                />
            );
        } else if (optionId === EDIT_POST) {
            return (
                <Modal isOpen={isConfirmationShown} onClose={handleClose}>
                    <StyledCommentModal
                        onClose={handleClose}
                        onCancel={handleClose}
                        onSubmit={handleSubmit}
                        initialCaption={
                            captionComment ? captionComment.commentText : ''
                        }
                        initialImage={activity.photoUrl}
                        onEdit={true}
                        PhotoProps={{
                            CropProps: {
                                minWidth: 100,
                                unit: '%',
                            },
                        }}
                    />
                </Modal>
            );
        }
        return null;
    }, [
        optionId,
        isConfirmationShown,
        content,
        isLoading,
        onSelect,
        t,
        handleSubmit,
        handleRemovePhoto,
        handleRemovePost,
        activity,
        captionComment,
        handleClose,
    ]);
    return (
        <>
            {getConfirmationModal()}
            <Grid alignItems="center" justify="center">
                <Button
                    id={id}
                    aria-controls={open ? id : ''}
                    aria-haspopup="true"
                    aria-expanded={open ? 'true' : ''}
                    onClick={handleClick}
                >
                    {isActionLoading ? <CircularProgress size={20} /> : '. . .'}
                </Button>
                <Menu
                    id="activity-options-menu"
                    aria-labelledby={id}
                    anchorEl={anchorEl}
                    open={open}
                    onClose={handleClose}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'left',
                    }}
                    transformOrigin={{
                        vertical: 'bottom',
                        horizontal: 'left',
                    }}
                >
                    {options.map((option, key) => {
                        return (
                            <MenuItem
                                key={key}
                                onClick={() => {
                                    setSelectedOptionId(option.id);
                                    setConfirmationShown(true);
                                }}
                            >
                                {option.label}
                            </MenuItem>
                        );
                    })}
                </Menu>
            </Grid>
        </>
    );
};

ActivityOptionsPopper.propTypes = {
    // pass back with the onSelect callback for context.
    content: PropTypes.object,
    // An object containing all the different options that will be available in the popper
    options: PropTypes.arrayOf(
        PropTypes.shape({
            // What is displayed to the user
            label: PropTypes.string,
            // a unique id for this option
            id: PropTypes.string,
        }),
    ),
    /*
     * Called whe one of the options in the popper is selected
     * passes the content and the option that was selcted as it's args
     */
    onSelect: PropTypes.func,
    isLoading: PropTypes.bool,
    activityId: PropTypes.string.isRequired,
    actionId: PropTypes.string.isRequired,
    updateActivity: PropTypes.func.isRequired,
    removePhoto: PropTypes.func.isRequired,
    removePost: PropTypes.func.isRequired,
    communityId: PropTypes.string.isRequired,
    activity: PropTypes.shape({
        comments: PropTypes.arrayOf(
            PropTypes.shape({
                commentText: PropTypes.string,
            }),
        ),
        caption: PropTypes.string,
        photoUrl: PropTypes.string,
    }).isRequired,
};

ActivityOptionsPopper.defaultProps = {
    content: {},
    options: [],
    onSelect: noop,
    isLoading: false,
};

const mapStateToProps = (state) => ({
    communityId: getActiveCommunityId(state),
});

const mapDispatchToProps = (dispatch) => ({
    updateActivity: ({ actionId, communityId, options, activityId }) => {
        dispatch(
            editActivityRequested({
                actionId,
                communityId,
                options,
                activityId,
            }),
        );
        dispatch(
            activityDataRequested(ACTIVITIES_FEED_TYPE_TRENDING, {
                communityId,
            }),
        );
    },
    removePhoto: ({ actionId, communityId, activityId }) => {
        dispatch(
            removePhotoRequested({
                actionId,
                communityId,
                activityId,
            }),
        );
        dispatch(
            activityDataRequested(ACTIVITIES_FEED_TYPE_TRENDING, {
                communityId,
            }),
        );
    },
    removePost: ({ actionId, communityId, activityId }) => {
        dispatch(
            removePostRequested({
                actionId,
                communityId,
                activityId,
            }),
        );
        dispatch(
            activityDataRequested(ACTIVITIES_FEED_TYPE_TRENDING, {
                communityId,
            }),
        );
    },
});

export default connect(
    mapStateToProps,
    mapDispatchToProps,
)(ActivityOptionsPopper);
